// ─────────────────────────────────────────────────────────────────────────────
// Onboarding de comercios — constantes y helpers compartidos.
//
// Funnel "prospecto-first" (espejo del mockup mu): todo comercio entra como
// PROSPECTO y avanza nuevo → contactado → validación → aprobado, con rechazado
// como estado terminal aparte.
//
// EN CONSTRUCCIÓN: el backend aún NO persiste `stage`, `progress`, `blocker`,
// `businessIdentifier`, `origin` ni marcas de tiempo por etapa (ver
// PROSPECTS-BACKEND.md). Por eso toda transición de etapa es OPTIMISTA en el
// front y NO persiste; el origen y los "días en etapa" son derivados/placeholder.
// El front degrada con gracia: si falta `stage` (o trae una etapa del esquema
// viejo kyc/documentos/contrato/integracion/completado) se mapea al equivalente
// más cercano del nuevo funnel y el progreso se DERIVA del índice de etapa.
// ─────────────────────────────────────────────────────────────────────────────

import { Inbox, Phone, FileSearch, UserCheck, XCircle } from "lucide-react";

export const PHONE_PREFIX = "+56 ";

// Nuevo funnel prospecto-first. `rechazado` es terminal (no es un paso lineal,
// pero se incluye en el orden para el stepper y como columna/tab de estado).
export const STAGE_ORDER = [
  "nuevo",
  "contactado",
  "validacion",
  "aprobado",
  "rechazado",
];

// Columnas del pipeline lineal (rechazado se muestra aparte).
export const PIPELINE_STAGES = ["nuevo", "contactado", "validacion", "aprobado"];

// Siguiente etapa del funnel lineal (para el CTA "Avanzar etapa").
export const NEXT_STAGE = {
  nuevo: "contactado",
  contactado: "validacion",
  validacion: "aprobado",
};

// Íconos sugeridos por etapa (lucide component refs).
export const STAGE_ICON = {
  nuevo: Inbox,
  contactado: Phone,
  validacion: FileSearch,
  aprobado: UserCheck,
  rechazado: XCircle,
};

// Progreso derivado por etapa cuando el backend no envía `progress`. rechazado
// queda "lleno" (terminal) pero con barra en tono error.
export const STAGE_PROGRESS = {
  nuevo: 0,
  contactado: 33,
  validacion: 66,
  aprobado: 100,
  rechazado: 100,
};

// Tono del Badge por etapa (tonos del mockup: nuevo=neutral, contactado=info,
// validacion=warning, aprobado=success, rechazado=error).
export const STAGE_TONE = {
  nuevo: "neutral",
  contactado: "info",
  validacion: "warning",
  aprobado: "success",
  rechazado: "error",
};

// Tono para KpiFilterCards (usa la misma paleta; card de total va en brand).
export const STAGE_KPI_TONE = {
  nuevo: "neutral",
  contactado: "info",
  validacion: "warning",
  aprobado: "success",
  rechazado: "error",
};

// Color de la barra de progreso por etapa (sincronizado con STAGE_TONE).
export const STAGE_BAR_CLASS = {
  nuevo: "bg-status-neutral",
  contactado: "bg-status-info",
  validacion: "bg-status-warning",
  aprobado: "bg-status-success",
  rechazado: "bg-status-error",
};

// Color del "chip" del ícono por etapa.
export const STAGE_CHIP_CLASS = {
  "": "bg-brand-50 text-brand-700",
  nuevo: "bg-status-neutral-soft text-fg-muted",
  contactado: "bg-status-info-soft text-status-info",
  validacion: "bg-status-warning-soft text-status-warning",
  aprobado: "bg-status-success-soft text-status-success",
  rechazado: "bg-status-error-soft text-status-error",
};

// Origen del prospecto (EN CONSTRUCCIÓN — el backend no lo guarda todavía).
export const ORIGIN_TONE = {
  "registro-web": "info",
  manual: "neutral",
};

// Mapa de degradación de las etapas del esquema viejo al nuevo funnel.
const LEGACY_STAGE_MAP = {
  kyc: "nuevo",
  documentos: "validacion",
  contrato: "validacion",
  integracion: "aprobado",
  completado: "aprobado",
};

export const emptyForm = {
  fullName: "",
  companyName: "",
  phone: PHONE_PREFIX,
  email: "",
  industry: "",
  website: "",
  notes: "",
  businessIdentifier: "",
  stage: "nuevo",
  blocker: "",
};

// Normaliza la etapa de un prospecto: acepta las nuevas, degrada las viejas y
// cae a 'nuevo' si falta o es inválida.
export const stageOf = (prospect) => {
  const s = prospect?.stage;
  if (STAGE_ORDER.includes(s)) return s;
  if (s && LEGACY_STAGE_MAP[s]) return LEGACY_STAGE_MAP[s];
  return "nuevo";
};

// Índice de etapa (0..4) para el stepper.
export const stageIndexOf = (prospect) =>
  Math.max(0, STAGE_ORDER.indexOf(stageOf(prospect)));

// Progreso 0-100: usa `progress` del backend si viene; si no, lo deriva.
export const progressOf = (prospect) => {
  if (
    typeof prospect?.progress === "number" &&
    !Number.isNaN(prospect.progress)
  ) {
    return Math.max(0, Math.min(100, Math.round(prospect.progress)));
  }
  return STAGE_PROGRESS[stageOf(prospect)] ?? 0;
};

// Origen del prospecto (EN CONSTRUCCIÓN — no persiste). Los prospectos creados
// desde el formulario del panel se asumen 'manual' hasta que exista el campo.
export const originOf = (prospect) => {
  const o = prospect?.origin || prospect?.source;
  return o === "registro-web" ? "registro-web" : "manual";
};

// Días en la etapa actual (EN CONSTRUCCIÓN — aproximación: no hay marca de
// tiempo por cambio de etapa, se deriva de updatedAt/createdAt).
export const daysInStageOf = (prospect) => {
  const ref = prospect?.updatedAt || prospect?.createdAt;
  if (!ref) return null;
  const ms = Date.now() - new Date(ref).getTime();
  if (Number.isNaN(ms) || ms < 0) return null;
  return Math.floor(ms / 86400000);
};

export const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("es-CL", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (err) {
    return "—";
  }
};

// ── Overrides de etapa (front-only, localStorage) ───────────────────────────
// El backend IGNORA `stage`: las transiciones optimistas se perdían al
// desmontar el tab (el refetch las revertía). Se persisten como overlay
// por-navegador (mapa prospectId → stage) y se aplican tras cada fetch —
// mismo patrón que el módulo de cumplimiento. Si el backend algún día
// devuelve la misma etapa que el override, el override se limpia solo.
const STAGE_OVERRIDES_KEY = "bt.prospects.stageOverrides";

export const readStageOverrides = () => {
  try {
    const raw = window.localStorage.getItem(STAGE_OVERRIDES_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch (err) {
    return {}; // parse/security errors → sin overrides
  }
};

const writeStageOverrides = (overrides) => {
  try {
    window.localStorage.setItem(STAGE_OVERRIDES_KEY, JSON.stringify(overrides));
  } catch (err) {
    /* quota/security: se ignora en silencio (front-only) */
  }
};

export const setStageOverride = (prospectId, stage) => {
  if (!prospectId || !STAGE_ORDER.includes(stage)) return;
  writeStageOverrides({ ...readStageOverrides(), [prospectId]: stage });
};

export const clearStageOverride = (prospectId) => {
  if (!prospectId) return;
  const overrides = readStageOverrides();
  if (!(prospectId in overrides)) return;
  delete overrides[prospectId];
  writeStageOverrides(overrides);
};

// Aplica el overlay a una lista recién fetcheada. Limpia los overrides que el
// backend ya refleja (stage computado == override) — quedan solo los vivos.
export const applyStageOverrides = (prospects) => {
  const overrides = readStageOverrides();
  if (!Object.keys(overrides).length) return prospects;
  return prospects.map((p) => {
    const override = overrides[p?._id];
    if (!override || !STAGE_ORDER.includes(override)) return p;
    if (stageOf(p) === override) {
      clearStageOverride(p._id); // el backend alcanzó al override
      return p;
    }
    return { ...p, stage: override };
  });
};

// Normaliza una URL/handle para abrirla (web o Instagram).
export const resolveWebsiteHref = (website) => {
  if (!website) return null;
  if (/^https?:\/\//i.test(website)) return website;
  if (website.startsWith("@")) return `https://instagram.com/${website.slice(1)}`;
  return `https://${website}`;
};

// ── Ofuscación (modo demo) ──────────────────────────────────────────────────
export const obfuscateText = (value) => {
  if (!value) return "";
  const trimmed = String(value).trim();
  if (!trimmed) return "";
  if (trimmed.length <= 2) return "•".repeat(trimmed.length);
  return trimmed[0] + "•".repeat(Math.min(trimmed.length - 1, 8));
};

export const obfuscatePhone = (value) => {
  if (!value) return "";
  const digits = String(value).replace(/\D/g, "");
  if (digits.length < 4) return "+•• •••• ••••";
  return `+•• ${digits.slice(0, 1)}••• •${digits.slice(-2)}`;
};

export const obfuscateEmail = (value) => {
  if (!value) return "";
  const [user, domain] = String(value).split("@");
  if (!domain) return obfuscateText(value);
  const userMasked = user.length > 2 ? user[0] + "•••" : "•••";
  const [domainName, ...rest] = domain.split(".");
  const tld = rest.join(".");
  const domainMasked = domainName.length > 1 ? domainName[0] + "•••" : "•••";
  return `${userMasked}@${domainMasked}${tld ? "." + tld : ""}`;
};
